import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlarmClock, AlertOctagon, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Kpi, Panel, Loading, ExportBtn, FilterBar, Field, NSelect, useFetch } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MaintenanceDialog, RemindersTable, HistoryTable } from "@/components/Maintenance";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function Entretien({ vtype }) {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [vid, setVid] = useState("");
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const vehicles = useFetch("/vehicles", { type: vtype });
  const rem = useFetch("/maintenance/reminders", { vehicle_type: vtype, vehicle_id: vid });
  const hist = useFetch("/maintenance", { vehicle_type: vtype, vehicle_id: vid });
  const vlist = (vehicles.data || []).filter((v) => v.status !== "sorti");
  const rows = rem.data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;
  const reload = () => {
    rem.reload();
    hist.reload();
  };
  const del = async (m) => {
    if (!window.confirm("Supprimer cet entretien ?")) return;
    try {
      await api.delete(`/maintenance/${m.id}`);
      toast.success("Entretien supprimé");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const title = vtype === "livraison" ? "Véhicules de livraison" : "Véhicules commerciaux";
  return (
    <div data-testid={`entretien-page-${vtype}`}>
      <PageHeader overline={title} title="Entretien">
        {admin && <ExportBtn path="/export/maintenance" params={{ vehicle_type: vtype }} filename={`entretiens-${vtype}.xlsx`} testid="export-maintenance" />}
        {admin && <Link to="/parametres/imports" className="inline-flex items-center gap-2 h-10 px-3.5 rounded-md border border-slate-300 bg-white text-sm font-semibold text-[#0E2D43] hover:border-[#0E2D43]" data-testid="maintenance-import-link"><Upload className="w-4 h-4" /> Importer</Link>}
        <button onClick={() => setEdit({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-sm font-bold transition-colors" data-testid="maintenance-create-button">
          <Plus className="w-4 h-4" /> Nouvel entretien
        </button>
      </PageHeader>
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Kpi testid="kpi-maint-overdue" label="En retard" value={count("en_retard")} icon={AlertOctagon} tone="danger" />
        <Kpi testid="kpi-maint-soon" label="À prévoir" value={count("bientot")} icon={AlarmClock} delay={1} />
        <Kpi testid="kpi-maint-ok" label="À jour" value={count("ok")} icon={CheckCircle2} tone="ok" delay={2} />
      </div>
      {vlist.length > 1 && (
        <FilterBar testid="maintenance-filters">
          <Field label="Véhicule"><NSelect value={vid} onChange={setVid} placeholder="Tous" testid="maintenance-filter-vehicle"
            options={vlist.map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand} ${v.model}` }))} /></Field>
          <Field label="Statut rappel"><NSelect value={status} onChange={setStatus} placeholder="Tous" testid="maintenance-filter-status"
            options={[{ value: "en_retard", label: "En retard" }, { value: "bientot", label: "À prévoir" }, { value: "ok", label: "À jour" }]} /></Field>
        </FilterBar>
      )}
      <Tabs defaultValue="reminders">
        <TabsList className="mb-3">
          <TabsTrigger value="reminders" data-testid="tab-reminders">Rappels & échéances</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-maintenance-history">Historique des entretiens</TabsTrigger>
        </TabsList>
        <Panel flush>
          <TabsContent value="reminders" className="m-0">
            {rem.loading ? <Loading /> : <RemindersTable rows={status ? rows.filter((r) => r.status === status) : rows} />}
          </TabsContent>
          <TabsContent value="history" className="m-0">
            {hist.loading ? <Loading /> : <HistoryTable rows={hist.data || []} admin={admin} onEdit={setEdit} onDelete={del} />}
          </TabsContent>
        </Panel>
      </Tabs>
      {edit && <MaintenanceDialog entry={edit} vehicles={vlist} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
